import {
  ajouterJours,
  ajouterMois,
  formaterJour,
  formaterMontant,
  jourAParis,
  lundiDeLaSemaine,
  numeroSemaine,
  STATUTS_ECHEANCE,
} from '@admin-pmvb/core'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { type Echeance, requetes } from '../api'
import { Frise, type LigneFrise } from '../composants/Frise'
import { Icone } from '../composants/Icone'
import { Semaine } from '../composants/Semaine'
import { Vide } from '../composants/Section'
import { EntetePage, Filtre, Interrupteur, Onglets } from '../composants/Ui'
import { initiales, jourCourt, LIBELLES_STATUT_ECHEANCE, nomMois, options } from '../libelles'

export type VueEcheancier = 'frise' | 'semaine' | 'agenda' | 'liste'

export function BadgeStatut({ statut }: { statut: Echeance['statut'] }) {
  return (
    <span className={`badge badge-statut statut-${statut}`}>
      {LIBELLES_STATUT_ECHEANCE[statut]}
    </span>
  )
}

export function datesEcheance(e: Pick<Echeance, 'date' | 'finFenetre'>): string {
  return e.finFenetre && e.finFenetre > e.date
    ? `du ${formaterJour(e.date)} au ${formaterJour(e.finFenetre)}`
    : formaterJour(e.date)
}

function LigneEcheance({ e, jour = false }: { e: Echeance; jour?: boolean }) {
  const aConfirmer = e.certitude === 'a_confirmer' && e.statut === 'a_venir'
  return (
    <Link
      to="/echeances/$id"
      params={{ id: e.id }}
      className={jour ? 'ligne-agenda' : 'ligne-agenda sans-jour'}
    >
      {jour && <span className="jour">{e.date.slice(8, 10)}</span>}
      <div className="ligne-contenu">
        <strong>
          {e.libelle} <span className="discret">{e.periode}</span>
          {e.verification && <span className="badge">Vérification</span>}
        </strong>
        <span className="discret">
          {[e.entite, e.finFenetre && e.finFenetre > e.date ? datesEcheance(e) : null]
            .concat(jour ? [] : [formaterJour(e.date)])
            .filter(Boolean)
            .join(' · ')}
        </span>
      </div>
      <span className="montant">
        {e.montantPrevuCts !== null ? formaterMontant(e.montantPrevuCts, e.devise) : ''}
      </span>
      {aConfirmer ? <span className="badge">À confirmer</span> : <BadgeStatut statut={e.statut} />}
    </Link>
  )
}

export function Echeancier({
  vue,
  onChangeVue,
}: {
  vue: VueEcheancier
  onChangeVue: (vue: VueEcheancier) => void
}) {
  const aujourdhui = jourAParis(new Date())
  const debut = `${aujourdhui.slice(0, 7)}-01`
  const echeances = useQuery(requetes.echeances({ du: debut, au: ajouterMois(debut, 12) }))
  const entites = useQuery(requetes.entites)
  const roles = useQuery(requetes.roles())
  const [entiteId, setEntiteId] = useState('')
  const [statut, setStatut] = useState('')
  const [texte, setTexte] = useState('')
  const [surcouche, setSurcouche] = useState(false)
  const [lundi, setLundi] = useState(() => lundiDeLaSemaine(aujourdhui))
  const dimanche = ajouterJours(lundi, 6)
  const echeancesSemaine = useQuery({
    ...requetes.echeances({ du: lundi, au: dimanche }),
    enabled: vue === 'semaine',
  })
  const enRetard = useQuery(requetes.paiements({ vue: 'en_retard' }))
  const tousPaiements = useQuery({ ...requetes.paiements({}), enabled: surcouche })
  const tachesOuvertes = useQuery({ ...requetes.taches({ vue: 'tableau' }), enabled: surcouche })
  const retards = new Set(
    (enRetard.data ?? []).map((p) => p.echeanceId).filter((x): x is string => Boolean(x)),
  )

  const filtrer = useMemo(() => {
    const t = texte.trim().toLowerCase()
    return (liste: Echeance[]) =>
      liste.filter(
        (e) =>
          (!entiteId || e.entiteId === entiteId) &&
          (!statut || e.statut === statut) &&
          (!t || `${e.libelle} ${e.entite} ${e.periode}`.toLowerCase().includes(t)),
      )
  }, [entiteId, statut, texte])
  const filtrees = useMemo(() => filtrer(echeances.data ?? []), [filtrer, echeances.data])
  const paiementsSurcouche = surcouche
    ? (tousPaiements.data ?? []).filter((p) => !['annule', 'rapproche'].includes(p.statut))
    : []
  const tachesSurcouche = surcouche
    ? (tachesOuvertes.data ?? []).filter((t) =>
        ['a_faire', 'en_cours', 'en_attente_tiers'].includes(t.statut),
      )
    : []

  const lignes: LigneFrise[] = (entites.data ?? [])
    .filter((e) => !e.archivee && (!entiteId || e.id === entiteId))
    .map((e) => ({
      entiteId: e.id,
      nom: e.nom,
      responsables: (roles.data ?? [])
        .filter((r) => r.entiteId === e.id && r.responsable)
        .map((r) => initiales(r.personne))
        .join(', '),
    }))

  const parMois = new Map<string, Echeance[]>()
  for (const e of filtrees) {
    const cle = e.date.slice(0, 7)
    parMois.set(cle, [...(parMois.get(cle) ?? []), e])
  }

  return (
    <main>
      <EntetePage
        surtitre="Douze mois"
        titre="Échéancier"
        action={{
          icone: 'event_available',
          libelle: 'Lien d’agenda',
          lien: '/reglages',
          recherche: { onglet: 'moi' },
        }}
      />
      <div className="pile">
        <div className="barre-outils gauche">
          <Onglets
            valeur={vue}
            onChange={onChangeVue}
            onglets={[
              { valeur: 'frise', libelle: 'Frise', icone: 'view_timeline' },
              { valeur: 'semaine', libelle: 'Semaine', icone: 'view_week' },
              { valeur: 'agenda', libelle: 'Agenda', icone: 'event_note' },
              { valeur: 'liste', libelle: 'Liste', icone: 'view_list' },
            ]}
          />
          <Filtre
            icone="domain"
            libelle="Entité"
            valeur={entiteId}
            onChange={setEntiteId}
            options={[
              { valeur: '', libelle: 'Toutes les entités' },
              ...(entites.data ?? [])
                .filter((e) => !e.archivee)
                .map((e) => ({ valeur: e.id, libelle: e.nom })),
            ]}
          />
          <Filtre
            icone="filter_list"
            libelle="Statut"
            valeur={statut}
            onChange={setStatut}
            options={[
              { valeur: '', libelle: 'Tous les statuts' },
              ...options(LIBELLES_STATUT_ECHEANCE).filter((o) =>
                (STATUTS_ECHEANCE as readonly string[]).includes(o.valeur),
              ),
            ]}
          />
          <label className="filtre">
            <Icone nom="search" />
            <input
              type="search"
              aria-label="Rechercher une échéance"
              value={texte}
              onChange={(e) => setTexte(e.target.value)}
              placeholder="TVA, CFE…"
            />
          </label>
          {(vue === 'frise' || vue === 'semaine') && (
            <span style={{ marginLeft: 'auto' }}>
              <Interrupteur actif={surcouche} onChange={setSurcouche}>
                Paiements et tâches
              </Interrupteur>
            </span>
          )}
        </div>

        {vue !== 'semaine' && echeances.isPending && <Vide>Chargement…</Vide>}
        {vue !== 'semaine' && echeances.data && filtrees.length === 0 && (
          <Vide>Aucune échéance pour ces filtres.</Vide>
        )}

        {vue === 'frise' && echeances.data && (
          <section className="carte">
            <Frise
              lignes={lignes}
              echeances={filtrees}
              debut={debut}
              aujourdhui={aujourdhui}
              retards={retards}
              paiements={paiementsSurcouche}
              taches={tachesSurcouche}
            />
          </section>
        )}

        {vue === 'semaine' && (
          <section className="carte">
            <div className="semaine-navigation">
              <button
                type="button"
                className="bouton-icone"
                aria-label="Semaine précédente"
                onClick={() => setLundi(ajouterJours(lundi, -7))}
              >
                <Icone nom="chevron_left" />
              </button>
              <button
                type="button"
                className="bouton-icone"
                aria-label="Semaine suivante"
                onClick={() => setLundi(ajouterJours(lundi, 7))}
              >
                <Icone nom="chevron_right" />
              </button>
              <h2>
                Semaine {numeroSemaine(lundi)}{' '}
                <span className="discret">
                  du {jourCourt(lundi)} au {jourCourt(dimanche)} {dimanche.slice(0, 4)}
                </span>
              </h2>
              {lundi !== lundiDeLaSemaine(aujourdhui) && (
                <button
                  type="button"
                  className="bouton-discret petit"
                  onClick={() => setLundi(lundiDeLaSemaine(aujourdhui))}
                >
                  Cette semaine
                </button>
              )}
            </div>
            {echeancesSemaine.isPending ? (
              <Vide>Chargement…</Vide>
            ) : (
              <Semaine
                lundi={lundi}
                aujourdhui={aujourdhui}
                echeances={filtrer(echeancesSemaine.data ?? [])}
                retards={retards}
                paiements={paiementsSurcouche.filter((p) => !entiteId || p.entiteId === entiteId)}
                taches={tachesSurcouche.filter((t) => !entiteId || t.entiteId === entiteId)}
              />
            )}
          </section>
        )}

        {vue === 'agenda' &&
          [...parMois.entries()].map(([mois, liste]) => (
            <section key={mois} className="carte carte-liste">
              <h2 className="titre-mois">{nomMois(mois)}</h2>
              {liste.map((e) => (
                <LigneEcheance key={e.id} e={e} jour />
              ))}
            </section>
          ))}

        {vue === 'liste' && filtrees.length > 0 && (
          <section className="carte carte-liste">
            <h2 className="titre-mois">{filtrees.length} échéances</h2>
            {filtrees.map((e) => (
              <LigneEcheance key={e.id} e={e} />
            ))}
          </section>
        )}
      </div>
    </main>
  )
}
